import { requireAdmin as requireMfaAdmin, requireCompletedProfile, } from "@hisani/auth";
import { IdSchema } from "@hisani/core";
import { z } from "zod";
import { PLANNER_ERROR_CODES, ConditionBookStatusSchema, PlannerPurseCurrencySchema, PlannerReviewStateSchema, PlannerCsvImportCommandSchema, RaceFieldsSchema, } from "./contracts.js";
import { invalidPlannerTransition as invalidTransition, plannerConflict as conflict, plannerError, } from "./errors.js";
function requireAdmin(principal) {
    return requireCompletedProfile(requireMfaAdmin(principal));
}
const reasonSchema = z
    .string()
    .trim()
    .min(5)
    .max(500)
    .refine((value) => !value.includes("\0"));
const placeNameSchema = z
    .string()
    .trim()
    .min(2)
    .max(120)
    .refine((value) => !value.includes("\0"));
export async function importPlannerConditionBook(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = PlannerCsvImportCommandSchema.parse(input);
    await requireCsvImportSchema(dependencies);
    const fingerprint = z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .parse(dependencies.fingerprint(JSON.stringify({
        title: parsed.title,
        racecourseId: parsed.racecourseId,
        sourceFilename: parsed.sourceFilename,
        sourceSize: parsed.sourceSize,
        rows: parsed.rows,
        reason: parsed.reason,
    })));
    const now = dependencies.clock.now();
    const imported = await dependencies.repository.importConditionBook({
        id: parsed.importId,
        racecourseId: parsed.racecourseId,
        title: parsed.title,
        sourceFilename: parsed.sourceFilename,
        sourceSize: parsed.sourceSize,
        fingerprint,
        rows: parsed.rows,
        actorId: principal.account.id,
        reason: parsed.reason,
        now,
    });
    if (imported.status === "IDEMPOTENCY_CONFLICT")
        throw plannerError(PLANNER_ERROR_CODES.importConflict, "This import ID was already used for different data.");
    if (imported.status === "PLACE_UNAVAILABLE")
        throw plannerError(PLANNER_ERROR_CODES.inactivePlace, "Select an active racecourse in an active jurisdiction.");
    return {
        asOf: now,
        book: projectConditionBook(imported.book),
        replayed: imported.replayed,
    };
}
export async function getPlannerAdminPlaces(principal, dependencies) {
    requireAdmin(principal);
    const places = await dependencies.repository.listPlaces();
    return {
        asOf: places.asOf,
        jurisdictions: places.jurisdictions.map(projectAdminJurisdiction),
        racecourses: places.racecourses.map(projectAdminRacecourse),
    };
}
export async function listPlannerAdminConditionBooks(principal, input, dependencies) {
    requireAdmin(principal);
    await requireCsvImportSchema(dependencies);
    const parsed = z
        .object({
        q: z.string().trim().min(1).max(100).optional(),
        status: ConditionBookStatusSchema.optional(),
        reviewState: PlannerReviewStateSchema.optional(),
        limit: z.coerce.number().int().min(1).max(50).default(25),
        beforeUpdatedAt: z.string().datetime({ offset: true }).optional(),
        beforeId: IdSchema.optional(),
    })
        .strict()
        .parse(input);
    const page = await dependencies.repository.listConditionBooks({
        ...(parsed.q === undefined ? {} : { query: parsed.q }),
        ...(parsed.status === undefined ? {} : { status: parsed.status }),
        ...(parsed.reviewState === undefined
            ? {}
            : { reviewState: parsed.reviewState }),
        limit: parsed.limit,
        ...adminCursor(parsed.beforeUpdatedAt, parsed.beforeId),
    });
    return {
        asOf: page.asOf,
        items: page.items.map(projectConditionBook),
        nextCursor: page.nextCursor === null
            ? null
            : {
                updatedAt: page.nextCursor.updatedAt,
                id: page.nextCursor.id,
            },
    };
}
export async function getPlannerAdminConditionBook(principal, id, input, dependencies) {
    requireAdmin(principal);
    await requireCsvImportSchema(dependencies);
    const parsed = z
        .object({
        draftLimit: z.coerce.number().int().min(1).max(50).default(25),
        draftBeforeUpdatedAt: z.string().datetime({ offset: true }).optional(),
        draftBeforeId: IdSchema.optional(),
    })
        .strict()
        .parse(input);
    const detail = await dependencies.repository.getConditionBookDetail({
        id: IdSchema.parse(id),
        drafts: {
            limit: parsed.draftLimit,
            ...adminCursor(parsed.draftBeforeUpdatedAt, parsed.draftBeforeId),
        },
    });
    if (detail === null) {
        throw plannerError(PLANNER_ERROR_CODES.notFound, "The requested Condition Book was not found.");
    }
    return {
        asOf: detail.asOf,
        book: projectConditionBook(detail.book),
        drafts: {
            items: detail.drafts.items.map(projectDraftRace),
            nextCursor: detail.drafts.nextCursor === null
                ? null
                : {
                    updatedAt: detail.drafts.nextCursor.updatedAt,
                    id: detail.drafts.nextCursor.id,
                },
        },
    };
}
export async function saveDraftRace(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = z
        .object({
        id: IdSchema.optional(),
        conditionBookId: IdSchema,
        expectedVersion: z.number().int().positive().optional(),
        fields: RaceFieldsSchema,
        reason: reasonSchema,
    })
        .parse(input);
    await requireCsvImportSchema(dependencies);
    const common = {
        ...parsed.fields,
        conditionBookId: parsed.conditionBookId,
        actorId: principal.account.id,
        reason: parsed.reason,
        now: dependencies.clock.now(),
    };
    const saved = parsed.id === undefined
        ? await dependencies.repository.createDraft({
            ...common,
            id: dependencies.newId(),
            sourceLocation: "Manual Admin row",
        })
        : await dependencies.repository.updateDraft({
            ...common,
            id: parsed.id,
            expectedVersion: parsed.expectedVersion ??
                (() => {
                    throw plannerError(PLANNER_ERROR_CODES.invalidRequest, "An expected version is required when editing a draft.");
                })(),
        });
    if (saved === null)
        throw conflict();
    return { asOf: common.now, draft: projectDraftRace(saved) };
}
export async function removeDraftRace(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = z
        .object({
        id: IdSchema,
        conditionBookId: IdSchema,
        expectedVersion: z.number().int().positive(),
        reason: reasonSchema,
    })
        .parse(input);
    await requireCsvImportSchema(dependencies);
    const now = dependencies.clock.now();
    const deleted = await dependencies.repository.deleteDraft({
        ...parsed,
        actorId: principal.account.id,
        now,
    });
    if (!deleted)
        throw conflict();
    return { asOf: now, deleted: true };
}
export async function movePlannerConditionBook(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = z
        .object({
        id: IdSchema,
        from: ConditionBookStatusSchema,
        to: ConditionBookStatusSchema,
        reason: reasonSchema,
    })
        .parse(input);
    const allowed = {
        DRAFT: ["IN_REVIEW", "REJECTED"],
        IN_REVIEW: ["DRAFT", "PUBLISHED", "REJECTED"],
        PUBLISHED: [],
        REJECTED: [],
    };
    if (!allowed[parsed.from].includes(parsed.to))
        throw invalidTransition();
    await requireCsvImportSchema(dependencies);
    const now = dependencies.clock.now();
    const result = parsed.to === "PUBLISHED"
        ? await dependencies.repository.publishConditionBook({
            id: parsed.id,
            actorId: principal.account.id,
            reason: parsed.reason,
            now,
        })
        : await dependencies.repository.transitionConditionBook({
            id: parsed.id,
            from: [parsed.from],
            to: parsed.to,
            actorId: principal.account.id,
            reason: parsed.reason,
            now,
        });
    if (result === null)
        throw invalidTransition();
    return { asOf: now, book: projectConditionBook(result) };
}
export async function createPlannerJurisdiction(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = z
        .object({
        code: z
            .string()
            .trim()
            .regex(/^[A-Z0-9]{2,10}$/),
        name: placeNameSchema,
        nameAr: placeNameSchema,
        timeZone: z.string().trim().min(1).max(100),
        currency: PlannerPurseCurrencySchema,
        reason: reasonSchema,
    })
        .parse(input);
    validateTimeZone(parsed.timeZone);
    const now = dependencies.clock.now();
    const jurisdiction = await dependencies.repository.createJurisdiction({
        id: dependencies.newId(),
        code: parsed.code,
        name: parsed.name,
        nameAr: parsed.nameAr,
        timeZone: parsed.timeZone,
        currency: parsed.currency,
        active: true,
        actorId: principal.account.id,
        reason: parsed.reason,
        now,
    });
    if (jurisdiction === null)
        throw conflict();
    return { asOf: now, jurisdiction: projectAdminJurisdiction(jurisdiction) };
}
export async function createPlannerRacecourse(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = z
        .object({
        jurisdictionId: IdSchema,
        code: z
            .string()
            .trim()
            .regex(/^[A-Z0-9]{2,10}$/),
        name: placeNameSchema,
        nameAr: placeNameSchema,
        reason: reasonSchema,
    })
        .parse(input);
    const now = dependencies.clock.now();
    const racecourse = await dependencies.repository.createRacecourse({
        id: dependencies.newId(),
        jurisdictionId: parsed.jurisdictionId,
        code: parsed.code,
        name: parsed.name,
        nameAr: parsed.nameAr,
        active: true,
        actorId: principal.account.id,
        reason: parsed.reason,
        now,
    });
    if (racecourse === null)
        throw conflict();
    return { asOf: now, racecourse: projectAdminRacecourse(racecourse) };
}
export async function updatePlannerJurisdiction(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = z
        .object({
        id: IdSchema,
        expectedUpdatedAt: z
            .string()
            .datetime({ offset: true })
            .transform((value) => new Date(value)),
        code: z
            .string()
            .trim()
            .regex(/^[A-Z0-9]{2,10}$/)
            .optional(),
        name: placeNameSchema.optional(),
        nameAr: placeNameSchema.optional(),
        timeZone: z.string().trim().min(1).max(100).optional(),
        currency: PlannerPurseCurrencySchema.optional(),
        active: z.boolean().optional(),
        reason: reasonSchema,
    })
        .refine((value) => value.code !== undefined ||
        value.name !== undefined ||
        value.nameAr !== undefined ||
        value.timeZone !== undefined ||
        value.currency !== undefined ||
        value.active !== undefined, { message: "At least one jurisdiction field is required." })
        .parse(input);
    if (parsed.timeZone !== undefined)
        validateTimeZone(parsed.timeZone);
    const now = dependencies.clock.now();
    const jurisdiction = await dependencies.repository.updateJurisdiction({
        ...parsed,
        actorId: principal.account.id,
        now,
    });
    if (jurisdiction === null)
        throw conflict();
    return { asOf: now, jurisdiction: projectAdminJurisdiction(jurisdiction) };
}
export async function updatePlannerRacecourse(principal, input, dependencies) {
    requireAdmin(principal);
    const parsed = z
        .object({
        id: IdSchema,
        expectedUpdatedAt: z
            .string()
            .datetime({ offset: true })
            .transform((value) => new Date(value)),
        code: z
            .string()
            .trim()
            .regex(/^[A-Z0-9]{2,10}$/)
            .optional(),
        name: placeNameSchema.optional(),
        nameAr: placeNameSchema.optional(),
        active: z.boolean().optional(),
        reason: reasonSchema,
    })
        .refine((value) => value.code !== undefined ||
        value.name !== undefined ||
        value.nameAr !== undefined ||
        value.active !== undefined, { message: "At least one racecourse field is required." })
        .parse(input);
    const now = dependencies.clock.now();
    const racecourse = await dependencies.repository.updateRacecourse({
        ...parsed,
        actorId: principal.account.id,
        now,
    });
    if (racecourse === null)
        throw conflict();
    return { asOf: now, racecourse: projectAdminRacecourse(racecourse) };
}
function projectJurisdiction(jurisdiction) {
    return {
        id: jurisdiction.id,
        code: jurisdiction.code,
        name: jurisdiction.name,
        nameAr: jurisdiction.nameAr,
        timeZone: jurisdiction.timeZone,
        currency: jurisdiction.currency,
        active: jurisdiction.active,
    };
}
function projectAdminJurisdiction(jurisdiction) {
    return {
        ...projectJurisdiction(jurisdiction),
        updatedAt: jurisdiction.updatedAt,
    };
}
function projectRacecourse(racecourse) {
    return {
        id: racecourse.id,
        jurisdictionId: racecourse.jurisdictionId,
        code: racecourse.code,
        name: racecourse.name,
        nameAr: racecourse.nameAr,
        active: racecourse.active,
    };
}
function projectAdminRacecourse(racecourse) {
    return {
        ...projectRacecourse(racecourse),
        updatedAt: racecourse.updatedAt,
    };
}
function projectConditionBook(book) {
    return {
        id: book.id,
        title: book.title,
        jurisdiction: projectJurisdiction(book.jurisdiction),
        racecourse: projectRacecourse(book.racecourse),
        source: {
            filename: book.source.filename,
            contentType: book.source.contentType,
            size: book.source.size,
            rowCount: book.source.rowCount,
        },
        status: book.status,
        draftWritesAllowed: book.draftWritesAllowed,
        draftRaceCount: book.draftRaceCount,
        unresolvedDraftRaceCount: book.unresolvedDraftRaceCount,
        reviewReady: book.reviewReady,
        createdAt: book.createdAt,
        updatedAt: book.updatedAt,
        publishedAt: book.publishedAt,
    };
}
function projectDraftRace(draft) {
    return {
        id: draft.id,
        conditionBookId: draft.conditionBookId,
        raceDate: draft.raceDate,
        track: draft.track,
        weekday: draft.weekday,
        meetingNumber: draft.meetingNumber,
        raceNumber: draft.raceNumber,
        raceName: draft.raceName,
        grade: draft.grade,
        surface: draft.surface,
        distanceMeters: draft.distanceMeters,
        breed: draft.breed,
        category: draft.category,
        handicapLow: draft.handicapLow,
        handicapHigh: draft.handicapHigh,
        benchmarkRating: draft.benchmarkRating,
        ageRestriction: draft.ageRestriction,
        minAge: draft.minAge,
        sexRestriction: draft.sexRestriction,
        uaeBredOnly: draft.uaeBredOnly,
        ratedMinimum: draft.ratedMinimum,
        privateOwnersOnly: draft.privateOwnersOnly,
        invitationOnly: draft.invitationOnly,
        purseMinor: draft.purseMinor,
        currency: draft.currency,
        purseAedMinor: draft.purseAedMinor,
        entryFee: draft.entryFee,
        declarationFee: draft.declarationFee,
        baseWeight: draft.baseWeight,
        penalties: draft.penalties,
        allowances: draft.allowances,
        eligibility: draft.eligibility,
        notes: draft.notes,
        ratingQualificationPublished: draft.ratingQualificationPublished,
        weightsPublished: draft.weightsPublished,
        entriesClose: draft.entriesClose,
        declarationsClose: draft.declarationsClose,
        jockeyDeclarationsClose: draft.jockeyDeclarationsClose,
        season: draft.season,
        sourceLocation: draft.sourceLocation,
        confidence: draft.confidence,
        correctedByHuman: draft.correctedByHuman,
        version: draft.version,
        validationIssues: [...draft.validationIssues],
        updatedAt: draft.updatedAt,
    };
}
function adminCursor(updatedAt, id) {
    if (updatedAt === undefined && id === undefined)
        return {};
    if (updatedAt === undefined || id === undefined) {
        throw plannerError(PLANNER_ERROR_CODES.invalidRequest, "Both cursor values are required.");
    }
    return { before: { updatedAt: new Date(updatedAt), id } };
}
function validateTimeZone(timeZone) {
    try {
        new Intl.DateTimeFormat("en", { timeZone }).format();
    }
    catch {
        throw plannerError(PLANNER_ERROR_CODES.invalidRequest, "The jurisdiction time zone is invalid.");
    }
}
async function requireCsvImportSchema(dependencies) {
    if (await dependencies.repository.isCsvImportSchemaReady())
        return;
    throw plannerError(PLANNER_ERROR_CODES.unavailable, "Planner CSV import is temporarily unavailable during deployment.");
}
